/**
 * ImportDialog — Upload Excel (.xlsx, .xls) → Preview → Validate → Import
 */
import { useState, useCallback } from 'react';
import { Upload, FileSpreadsheet, AlertCircle, CheckCircle2, Loader2, X } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
    parseExcelFile,
    validateExcelRows,
    type ExcelColumnDef as ColumnDef,
    type ParseExcelResult as ParseResult,
    type ExcelValidateResult as ValidateResult,
} from '@/lib/excel.utils';
import { validateExcelFile, extractErrorMessage } from '@/lib/utils';

interface ImportDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    columns: ColumnDef[];
    title: string;
    onConfirmImport: (validRows: Record<string, unknown>[]) => Promise<void>;
    onDownloadTemplate?: () => void;
}

type ImportStep = 'upload' | 'preview' | 'importing' | 'done';

export function ImportDialog({
    open,
    onOpenChange,
    columns,
    title,
    onConfirmImport,
    onDownloadTemplate,
}: ImportDialogProps) {
    const [step, setStep] = useState<ImportStep>('upload');
    const [parseResult, setParseResult] = useState<ParseResult | null>(null);
    const [validateResult, setValidateResult] = useState<ValidateResult | null>(null);
    const [fileName, setFileName] = useState('');
    const [error, setError] = useState('');
    const [dragOver, setDragOver] = useState(false);

    const reset = useCallback(() => {
        setStep('upload');
        setParseResult(null);
        setValidateResult(null);
        setFileName('');
        setError('');
        setDragOver(false);
    }, []);

    const handleClose = (open: boolean) => {
        if (!open) reset();
        onOpenChange(open);
    };

    const processFile = async (file: File) => {
        setError('');
        setFileName(file.name);

        const validation = validateExcelFile(file);
        if (!validation.valid) {
            setError(validation.error);
            return;
        }

        try {
            const parsed = await parseExcelFile(file);
            if (parsed.rows.length === 0) {
                setError('File không có dữ liệu hoặc sai định dạng.');
                return;
            }

            setParseResult(parsed);

            // Validate
            const validated = validateExcelRows(parsed.rows, columns);
            setValidateResult(validated);
            setStep('preview');
        } catch (err) {
            setError(`Lỗi đọc file: ${extractErrorMessage(err)}`);
        }
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) processFile(file);
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        setDragOver(false);
        const file = e.dataTransfer.files?.[0];
        if (file) processFile(file);
    };

    const handleImport = async () => {
        if (!validateResult || validateResult.validRows.length === 0) return;
        setStep('importing');
        try {
            await onConfirmImport(validateResult.validRows);
            setStep('done');
        } catch (err) {
            setError(`Lỗi import: ${err instanceof Error ? err.message : 'Unknown error'}`);
            setStep('preview');
        }
    };

    return (
        <Dialog open={open} onOpenChange={handleClose}>
            <DialogContent className="max-w-3xl sm:max-w-3xl max-h-[85vh] flex flex-col">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <FileSpreadsheet className="h-4 w-4 text-emerald-600" />
                        Import {title}
                    </DialogTitle>
                </DialogHeader>

                {/* Step: Upload */}
                {step === 'upload' && (
                    <div className="flex-1 flex flex-col gap-3">
                        <div
                            className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors cursor-pointer ${dragOver
                                    ? 'border-emerald-400 bg-emerald-50'
                                    : 'border-slate-200 hover:border-slate-300'
                                }`}
                            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                            onDragLeave={() => setDragOver(false)}
                            onDrop={handleDrop}
                            onClick={() => document.getElementById('import-file-input')?.click()}
                        >
                            <Upload className="h-10 w-10 text-slate-400 mx-auto mb-3" />
                            <p className="text-sm text-slate-600 font-medium">
                                Kéo thả file Excel vào đây hoặc <span className="text-indigo-600 underline">chọn file</span>
                            </p>
                            <p className="text-xs text-slate-400 mt-1">
                                Chỉ hỗ trợ: .xlsx, .xls (Excel)
                            </p>
                            <input
                                id="import-file-input"
                                type="file"
                                accept=".xlsx,.xls"
                                className="hidden"
                                onChange={handleFileChange}
                            />
                        </div>

                        {error && (
                            <div className="flex items-start gap-2 text-sm text-red-600 bg-red-50 p-3 rounded-lg">
                                <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                                <span>{error}</span>
                            </div>
                        )}

                        <div className="text-xs text-slate-500 space-y-2">
                            <p><strong>Lưu ý:</strong></p>
                            <ul className="list-disc ml-4 space-y-0.5">
                                <li>Dòng đầu tiên là header (tên cột)</li>
                                <li>Các cột có dấu (*) là bắt buộc</li>
                            </ul>
                            {onDownloadTemplate && (
                                <div className="mt-4 flex justify-center">
                                    <Button variant="outline" size="sm" onClick={onDownloadTemplate} className="gap-2">
                                        <FileSpreadsheet className="h-4 w-4 text-emerald-600" /> Tải form mẫu Excel
                                    </Button>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Step: Preview */}
                {step === 'preview' && parseResult && validateResult && (
                    <div className="flex-1 flex flex-col gap-3 overflow-hidden">
                        {/* Stats */}
                        <div className="flex items-center gap-3 flex-wrap">
                            <Badge variant="secondary" className="text-xs">
                                📄 {fileName}
                            </Badge>
                            <Badge variant="secondary" className="text-xs">
                                Tổng: {parseResult.totalRows} dòng
                            </Badge>
                            <Badge className="bg-emerald-100 text-emerald-800 text-xs">
                                <CheckCircle2 className="h-3 w-3 mr-1" />
                                Hợp lệ: {validateResult.validRows.length}
                            </Badge>
                            {validateResult.errors.length > 0 && (
                                <Badge className="bg-red-100 text-red-800 text-xs">
                                    <AlertCircle className="h-3 w-3 mr-1" />
                                    Lỗi: {validateResult.errors.length}
                                </Badge>
                            )}
                        </div>

                        {/* Preview Table */}
                        <ScrollArea className="flex-1 max-h-[300px] border rounded-lg">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead className="w-10 text-xs">#</TableHead>
                                        {columns.slice(0, 6).map(col => (
                                            <TableHead key={col.key} className="text-xs whitespace-nowrap">
                                                {col.label}
                                                {col.required && <span className="text-red-500 ml-0.5">*</span>}
                                            </TableHead>
                                        ))}
                                        {columns.length > 6 && (
                                            <TableHead className="text-xs text-slate-400">+{columns.length - 6} cột</TableHead>
                                        )}
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {parseResult.rows.slice(0, 5).map((row, idx) => (
                                        <TableRow key={idx}>
                                            <TableCell className="text-xs text-slate-400">{idx + 1}</TableCell>
                                            {columns.slice(0, 6).map(col => (
                                                <TableCell key={col.key} className="text-xs max-w-[150px] break-words whitespace-normal">
                                                    {row[col.label] || row[col.key] || '—'}
                                                </TableCell>
                                            ))}
                                            {columns.length > 6 && <TableCell className="text-xs text-slate-400">…</TableCell>}
                                        </TableRow>
                                    ))}
                                    {parseResult.totalRows > 5 && (
                                        <TableRow>
                                            <TableCell colSpan={Math.min(columns.length, 6) + 2} className="text-center text-xs text-slate-400 py-2">
                                                ... và {parseResult.totalRows - 5} dòng nữa
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </ScrollArea>

                        {/* Errors */}
                        {validateResult.errors.length > 0 && (
                            <ScrollArea className="max-h-[120px] border border-red-200 rounded-lg bg-red-50 p-2">
                                <div className="space-y-1">
                                    {validateResult.errors.slice(0, 10).map((err, i) => (
                                        <div key={i} className="text-xs text-red-700 flex gap-1">
                                            <span className="font-mono text-red-500 shrink-0">Dòng {err.row}:</span>
                                            <span>{err.column} — {err.message}</span>
                                        </div>
                                    ))}
                                    {validateResult.errors.length > 10 && (
                                        <div className="text-xs text-red-500 font-medium">
                                            ... và {validateResult.errors.length - 10} lỗi nữa
                                        </div>
                                    )}
                                </div>
                            </ScrollArea>
                        )}

                        {error && (
                            <div className="flex items-start gap-2 text-sm text-red-600 bg-red-50 p-3 rounded-lg">
                                <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                                <span>{error}</span>
                            </div>
                        )}
                    </div>
                )}

                {/* Step: Importing */}
                {step === 'importing' && (
                    <div className="flex-1 flex flex-col items-center justify-center gap-3 py-8">
                        <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
                        <p className="text-sm text-slate-600">Đang import {validateResult?.validRows.length} dòng...</p>
                    </div>
                )}

                {/* Step: Done */}
                {step === 'done' && (
                    <div className="flex-1 flex flex-col items-center justify-center gap-3 py-8">
                        <CheckCircle2 className="h-10 w-10 text-emerald-500" />
                        <p className="text-sm font-medium text-slate-900">Import thành công!</p>
                        <p className="text-xs text-slate-500">
                            Đã import {validateResult?.validRows.length} dòng dữ liệu.
                        </p>
                    </div>
                )}

                {/* Footer */}
                <DialogFooter className="gap-2">
                    {step === 'preview' && (
                        <>
                            <Button variant="ghost" size="sm" onClick={reset} className="gap-1">
                                <X className="h-3.5 w-3.5" /> Chọn lại
                            </Button>
                            <Button
                                size="sm"
                                onClick={handleImport}
                                disabled={!validateResult || validateResult.validRows.length === 0}
                                className="gap-1 bg-emerald-600 hover:bg-emerald-700"
                            >
                                <Upload className="h-3.5 w-3.5" />
                                Import {validateResult?.validRows.length} dòng hợp lệ
                            </Button>
                        </>
                    )}
                    {step === 'done' && (
                        <Button size="sm" onClick={() => handleClose(false)}>
                            Đóng
                        </Button>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
